// Availability Service - Business Logic and Data Validation
import { 
  AvailabilityDate, 
  AvailabilitySettings, 
  TimeSlot, 
  AvailabilityStatus,
  AvailabilityValidationResult,
  AvailabilityOperations,
  createEmptyAvailabilityDate,
  createDefaultAvailabilitySettings,
  getAvailabilityStatus,
  isDateAvailable,
  resolveMaxBookingsForDate,
  getDateKey as toLocalDateKey
} from '../types';
import {
  getAvailabilityDate,
  getAvailabilityRange,
  updateAvailabilityDate,
  bulkUpdateAvailability,
  getAvailabilitySettings,
  updateAvailabilitySettings,
  initializeDefaultAvailabilitySettings,
  isDateAvailableForBooking,
  getAvailableTimeSlots,
  incrementBookingCount,
  decrementBookingCount
} from './firebase';

// ============================================================================
// AVAILABILITY SERVICE CLASS
// ============================================================================

export class AvailabilityService implements AvailabilityOperations {
  private static instance: AvailabilityService;
  private settings: AvailabilitySettings | null = null;
  private cache: Map<string, AvailabilityDate> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  public static getInstance(): AvailabilityService {
    if (!AvailabilityService.instance) {
      AvailabilityService.instance = new AvailabilityService();
    }
    return AvailabilityService.instance;
  }

  // ============================================================================
  // CORE AVAILABILITY OPERATIONS
  // ============================================================================

  async createAvailabilityDate(date: Date, settings: Partial<AvailabilityDate> = {}): Promise<AvailabilityDate> {
    // Validate input
    const validation = this.validateAvailabilityDate(date, settings);
    if (!validation.isValid) {
      throw new Error(`Invalid availability data: ${validation.errors.join(', ')}`);
    }

    // Check if date already exists
    const existing = await this.getAvailabilityDate(date);
    if (existing) {
      throw new Error(`Availability already exists for date ${date.toISOString().split('T')[0]}`);
    }

    // Create new availability with defaults
    const newAvailability = {
      ...createEmptyAvailabilityDate(date),
      ...settings,
      // Apply default settings if not provided
      maxBookings: settings.maxBookings !== undefined
        ? settings.maxBookings
        : resolveMaxBookingsForDate(date, await this.getAvailabilitySettings())
    };

    // Save to Firebase
    await updateAvailabilityDate(date, newAvailability);

    // Update cache
    this.cache.set(this.getDateKey(date), newAvailability);

    return newAvailability;
  }

  async updateAvailabilityDate(id: string, updates: Partial<AvailabilityDate>): Promise<void> {
    // Validate updates
    const validation = this.validateAvailabilityUpdates(updates);
    if (!validation.isValid) {
      throw new Error(`Invalid update data: ${validation.errors.join(', ')}`);
    }

    // Get current date from ID
    const date = new Date(id);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date ID: ${id}`);
    }

    // Update in Firebase
    await updateAvailabilityDate(date, updates);

    // Update cache
    const cached = this.cache.get(id);
    if (cached) {
      const updated = { ...cached, ...updates, updatedAt: new Date() };
      this.cache.set(id, updated);
    } else {
      // Remove from cache to force refresh
      this.cache.delete(id);
    }
  }

  async deleteAvailabilityDate(id: string): Promise<void> {
    const date = new Date(id);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date ID: ${id}`);
    }

    // Delete from Firebase
    await updateAvailabilityDate(date, { isAvailable: false, isBlocked: true });

    // Remove from cache
    this.cache.delete(id);
  }

  async getAvailabilityDate(date: Date): Promise<AvailabilityDate | null> {
    const dateKey = this.getDateKey(date);
    
    // Check cache first
    if (this.isCacheValid(dateKey)) {
      return this.cache.get(dateKey) || null;
    }

    // Fetch from Firebase
    const availability = await getAvailabilityDate(date);
    
    // Update cache
    if (availability) {
      this.cache.set(dateKey, availability);
      this.cacheExpiry.set(dateKey, Date.now() + this.CACHE_DURATION);
    }

    return availability;
  }

  async getAvailabilityRange(startDate: Date, endDate: Date): Promise<AvailabilityDate[]> {
    // Validate date range
    if (startDate > endDate) {
      throw new Error('Start date must be before end date');
    }

    // Check if range is too large (prevent performance issues)
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      throw new Error('Date range cannot exceed 365 days');
    }

    // Fetch from Firebase
    const availabilityDates = await getAvailabilityRange(startDate, endDate);

    // Update cache
    availabilityDates.forEach(availability => {
      const dateKey = this.getDateKey(availability.date);
      this.cache.set(dateKey, availability);
      this.cacheExpiry.set(dateKey, Date.now() + this.CACHE_DURATION);
    });

    return availabilityDates;
  }

  async bulkUpdateAvailability(dates: Date[], updates: Partial<AvailabilityDate>): Promise<void> {
    // Validate input
    if (dates.length === 0) {
      throw new Error('No dates provided for bulk update');
    }

    if (dates.length > 100) {
      throw new Error('Cannot update more than 100 dates at once');
    }

    const validation = this.validateAvailabilityUpdates(updates);
    if (!validation.isValid) {
      throw new Error(`Invalid update data: ${validation.errors.join(', ')}`);
    }

    // Update in Firebase
    await bulkUpdateAvailability(dates, updates);

    // Update cache
    dates.forEach(date => {
      const dateKey = this.getDateKey(date);
      const cached = this.cache.get(dateKey);
      if (cached) {
        const updated = { ...cached, ...updates, updatedAt: new Date() };
        this.cache.set(dateKey, updated);
      } else {
        // Remove from cache to force refresh
        this.cache.delete(dateKey);
      }
    });
  }

  // ============================================================================
  // SETTINGS OPERATIONS
  // ============================================================================

  async getAvailabilitySettings(): Promise<AvailabilitySettings> {
    if (this.settings) {
      return this.settings;
    }

    let settings = await getAvailabilitySettings();
    
    if (!settings) {
      settings = await initializeDefaultAvailabilitySettings();
    }

    this.settings = settings;
    return settings;
  }

  async updateAvailabilitySettings(settings: Partial<AvailabilitySettings>): Promise<void> {
    // Validate settings
    const validation = this.validateSettings(settings);
    if (!validation.isValid) {
      throw new Error(`Invalid settings: ${validation.errors.join(', ')}`);
    }

    // Update in Firebase
    await updateAvailabilitySettings(settings);

    // Update local settings
    if (this.settings) {
      this.settings = { ...this.settings, ...settings, updatedAt: new Date() };
    } else {
      this.settings = await this.getAvailabilitySettings();
    }
  }

  // ============================================================================
  // BUSINESS LOGIC METHODS
  // ============================================================================

  async isDateAvailableForBooking(date: Date): Promise<boolean> {
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return false;
    }

    // Check against settings
    const settings = await this.getAvailabilitySettings();
    
    // Check advance booking limit
    const maxAdvanceDate = new Date();
    maxAdvanceDate.setDate(maxAdvanceDate.getDate() + settings.advanceBookingDays);
    if (date > maxAdvanceDate) {
      return false;
    }

    // Check working days
    const dayOfWeek = date.getDay();
    if (!settings.workingDays.includes(dayOfWeek)) {
      return false;
    }

    // Check blocked dates
    const isBlocked = settings.blockedDates.some(blockedDate => 
      blockedDate.toDateString() === date.toDateString()
    );
    if (isBlocked) {
      return false;
    }

    // Check specific availability
    const availability = await this.getAvailabilityDate(date);
    if (availability) {
      return isDateAvailable(availability, settings);
    }

    const maxBookings = resolveMaxBookingsForDate(date, settings);
    if (maxBookings != null && maxBookings <= 0) {
      return false;
    }

    return true; // Default to available if no specific record
  }

  async getAvailableTimeSlots(date: Date): Promise<TimeSlot[]> {
    const availability = await this.getAvailabilityDate(date);
    
    if (!availability || !availability.timeSlots) {
      return [];
    }

    return availability.timeSlots.filter(slot => 
      slot.isAvailable && 
      (!slot.maxBookings || slot.currentBookings < slot.maxBookings)
    );
  }

  async incrementBookingCount(date: Date, timeSlotId?: string): Promise<void> {
    const availability = await this.getAvailabilityDate(date);
    
    if (!availability) {
      // Create new availability record
      const newAvailability = createEmptyAvailabilityDate(date);
      newAvailability.currentBookings = 1;
      
      if (timeSlotId) {
        newAvailability.timeSlots = [{
          id: timeSlotId,
          startTime: '09:00',
          endTime: '10:00',
          isAvailable: true,
          currentBookings: 1
        }];
      }
      
      await this.createAvailabilityDate(date, newAvailability);
      return;
    }

    // Check if we can increment
    const maxBookings = resolveMaxBookingsForDate(
      date,
      await this.getAvailabilitySettings(),
      availability
    );
    if (maxBookings != null && availability.currentBookings >= maxBookings) {
      throw new Error('Maximum bookings reached for this date');
    }

    // Update booking count
    const updates: Partial<AvailabilityDate> = {
      currentBookings: availability.currentBookings + 1
    };

    // Update specific time slot if provided
    if (timeSlotId && availability.timeSlots) {
      const slot = availability.timeSlots.find(s => s.id === timeSlotId);
      if (slot) {
        if (slot.maxBookings && slot.currentBookings >= slot.maxBookings) {
          throw new Error('Maximum bookings reached for this time slot');
        }
        
        updates.timeSlots = availability.timeSlots.map(s => 
          s.id === timeSlotId 
            ? { ...s, currentBookings: s.currentBookings + 1 }
            : s
        );
      }
    }

    await this.updateAvailabilityDate(this.getDateKey(date), updates);
  }

  async decrementBookingCount(date: Date, timeSlotId?: string): Promise<void> {
    const availability = await this.getAvailabilityDate(date);
    
    if (!availability || availability.currentBookings === 0) {
      return; // Nothing to decrement
    }

    const updates: Partial<AvailabilityDate> = {
      currentBookings: Math.max(0, availability.currentBookings - 1)
    };

    // Update specific time slot if provided
    if (timeSlotId && availability.timeSlots) {
      updates.timeSlots = availability.timeSlots.map(slot => 
        slot.id === timeSlotId 
          ? { ...slot, currentBookings: Math.max(0, slot.currentBookings - 1) }
          : slot
      );
    }

    await this.updateAvailabilityDate(this.getDateKey(date), updates);
  }

  async recordConfirmedOrderBooking(date: Date): Promise<void> {
    await incrementBookingCount(date);
    this.cache.delete(this.getDateKey(date));
  }

  async releaseConfirmedOrderBooking(date: Date): Promise<void> {
    await decrementBookingCount(date);
    this.cache.delete(this.getDateKey(date));
  }

  // ============================================================================
  // VALIDATION METHODS
  // ============================================================================

  private validateAvailabilityDate(date: Date, data: Partial<AvailabilityDate>): AvailabilityValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate date
    if (isNaN(date.getTime())) {
      errors.push('Invalid date provided');
    }

    // Validate maxBookings
    if (data.maxBookings !== undefined) {
      if (data.maxBookings < 0) {
        errors.push('Max bookings cannot be negative');
      }
      if (data.maxBookings > 1000) {
        warnings.push('Max bookings is unusually high');
      }
    }

    // Validate currentBookings
    if (data.currentBookings !== undefined) {
      if (data.currentBookings < 0) {
        errors.push('Current bookings cannot be negative');
      }
      if (data.maxBookings && data.currentBookings > data.maxBookings) {
        errors.push('Current bookings cannot exceed max bookings');
      }
    }

    // Validate time slots
    if (data.timeSlots) {
      data.timeSlots.forEach((slot, index) => {
        if (!slot.id) {
          errors.push(`Time slot ${index + 1} missing ID`);
        }
        if (!slot.startTime || !slot.endTime) {
          errors.push(`Time slot ${index + 1} missing start or end time`);
        }
        if (slot.startTime && slot.endTime && slot.startTime >= slot.endTime) {
          errors.push(`Time slot ${index + 1} start time must be before end time`);
        }
        if (slot.maxBookings !== undefined && slot.maxBookings < 0) {
          errors.push(`Time slot ${index + 1} max bookings cannot be negative`);
        }
        if (slot.currentBookings !== undefined && slot.currentBookings < 0) {
          errors.push(`Time slot ${index + 1} current bookings cannot be negative`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateAvailabilityUpdates(updates: Partial<AvailabilityDate>): AvailabilityValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate maxBookings
    if (updates.maxBookings !== undefined) {
      if (updates.maxBookings < 0) {
        errors.push('Max bookings cannot be negative');
      }
      if (updates.maxBookings > 1000) {
        warnings.push('Max bookings is unusually high');
      }
    }

    // Validate currentBookings
    if (updates.currentBookings !== undefined) {
      if (updates.currentBookings < 0) {
        errors.push('Current bookings cannot be negative');
      }
    }

    // Validate time slots
    if (updates.timeSlots) {
      updates.timeSlots.forEach((slot, index) => {
        if (!slot.id) {
          errors.push(`Time slot ${index + 1} missing ID`);
        }
        if (!slot.startTime || !slot.endTime) {
          errors.push(`Time slot ${index + 1} missing start or end time`);
        }
        if (slot.startTime && slot.endTime && slot.startTime >= slot.endTime) {
          errors.push(`Time slot ${index + 1} start time must be before end time`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateSettings(settings: Partial<AvailabilitySettings>): AvailabilityValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate defaultMaxBookings
    if (settings.defaultMaxBookings !== undefined) {
      if (settings.defaultMaxBookings < 0) {
        errors.push('Default max bookings cannot be negative');
      }
      if (settings.defaultMaxBookings > 1000) {
        warnings.push('Default max bookings is unusually high');
      }
    }

    if (settings.weekdayMaxBookings !== undefined) {
      if (settings.weekdayMaxBookings < 0) {
        errors.push('Weekday max bookings cannot be negative');
      }
      if (settings.weekdayMaxBookings > 1000) {
        warnings.push('Weekday max bookings is unusually high');
      }
    }

    if (settings.weekendMaxBookings !== undefined) {
      if (settings.weekendMaxBookings < 0) {
        errors.push('Weekend max bookings cannot be negative');
      }
      if (settings.weekendMaxBookings > 1000) {
        warnings.push('Weekend max bookings is unusually high');
      }
    }

    // Validate advanceBookingDays
    if (settings.advanceBookingDays !== undefined) {
      if (settings.advanceBookingDays < 0) {
        errors.push('Advance booking days cannot be negative');
      }
      if (settings.advanceBookingDays > 365) {
        warnings.push('Advance booking days is unusually high');
      }
    }

    // Validate workingDays
    if (settings.workingDays !== undefined) {
      if (!Array.isArray(settings.workingDays)) {
        errors.push('Working days must be an array');
      } else {
        settings.workingDays.forEach(day => {
          if (day < 0 || day > 6) {
            errors.push('Working days must be between 0 (Sunday) and 6 (Saturday)');
          }
        });
      }
    }

    // Validate workingHours
    if (settings.workingHours !== undefined) {
      if (!settings.workingHours.start || !settings.workingHours.end) {
        errors.push('Working hours must have both start and end times');
      } else {
        if (settings.workingHours.start >= settings.workingHours.end) {
          errors.push('Working hours start time must be before end time');
        }
      }
    }

    // Validate timeSlotDuration
    if (settings.timeSlotDuration !== undefined) {
      if (settings.timeSlotDuration < 15) {
        errors.push('Time slot duration must be at least 15 minutes');
      }
      if (settings.timeSlotDuration > 480) {
        warnings.push('Time slot duration is unusually long');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private getDateKey(date: Date): string {
    return toLocalDateKey(date);
  }

  private isCacheValid(key: string): boolean {
    const expiry = this.cacheExpiry.get(key);
    return expiry ? Date.now() < expiry : false;
  }

  // Clear cache (useful for testing or when data might be stale)
  public clearCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  // Get cache statistics (useful for debugging)
  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const availabilityService = AvailabilityService.getInstance();

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

export const createAvailabilityService = () => AvailabilityService.getInstance();

export const getAvailabilityService = () => availabilityService;
